import { useMemo, useState } from "react";
import styles from "./SettingsAutopaySubscriptionPage.module.css";
import { useSettingsAutopaySubscriptionViewModel } from "@/features/settings/viewModal/useSettingsAutopaySubscriptionViewModel";
import { toastService } from "@/shared/toast";
import { usePropertyStreetViews, type PropertyLike } from "@/shared/utils";
import settingsHeaderHouse from "@/assets/images/settings-house.png";
import propFallback1 from "@/assets/images/prop-fallback-1.jpg";
import propFallback2 from "@/assets/images/prop-fallback-2.jpg";
import realisticHouse from "@/assets/images/realistic-house.jpg";

const FALLBACK_HOUSE_IMAGES = [propFallback1, propFallback2, realisticHouse];

export const SettingsAutopaySubscriptionPage = () => {
  const { loading, cards } = useSettingsAutopaySubscriptionViewModel();
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);

  // Dynamic Google Street View images for properties
  const propertyList = useMemo<PropertyLike[]>(() => {
    return cards.map((c) => ({
      property_id: c.propertyId,
      address: c.propertyAddress,
    }));
  }, [cards]);

  const streetViewImages = usePropertyStreetViews(propertyList);

  const handleCopyPropertyId = async (propertyId: string) => {
    if (!propertyId || propertyId === "-") return;
    try {
      await navigator.clipboard.writeText(propertyId);
      toastService.show("Property ID copied to clipboard!", "success");
    } catch {
      toastService.show("Could not copy Property ID", "error");
    }
  };

  return (
    <div className={styles.page}>
      {/* Header Banner matching user interface mockup */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIconWrap} aria-hidden="true">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#166534"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect width="20" height="14" x="2" y="5" rx="2" />
              <line x1="2" x2="22" y1="10" y2="10" />
            </svg>
          </div>

          <div className={styles.headerTitles}>
            <h1 className={styles.title}>
              Auto-Pay <span className={styles.titleAccent}>Subscriptions</span>
            </h1>
            <p className={styles.subtitle}>
              Manage your property subscriptions and billing preferences
            </p>
          </div>
        </div>

        <div className={styles.headerIllustrationWrap}>
          <img
            src={settingsHeaderHouse}
            alt=""
            className={styles.headerIllustration}
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Dismissable Info Notice Banner */}
      {!isBannerDismissed && (
        <section className={styles.infoBanner} aria-label="Subscription renewal info">
          <div className={styles.infoBannerIcon} aria-hidden="true">
            <span className={styles.infoI}>i</span>
          </div>

          <div className={styles.infoBannerContent}>
            <p className={styles.infoBannerHeading}>
              Your subscriptions will automatically renew each year.
            </p>
            <p className={styles.infoBannerSubtext}>
              You can cancel anytime, and your subscription will remain active until the end of the
              current billing period.
            </p>
          </div>

          <button
            type="button"
            className={styles.infoBannerClose}
            onClick={() => setIsBannerDismissed(true)}
            aria-label="Dismiss banner"
          >
            ✕
          </button>
        </section>
      )}

      {/* Loading Skeleton State */}
      {loading && (
        <div className={styles.loadingSkeletonList} aria-label="Loading subscriptions">
          {[1, 2, 3].map((key) => (
            <div key={key} className={`${styles.subscriptionCard} ${styles.skeletonCard}`}>
              <div className={styles.skeletonImage} />
              <div className={styles.skeletonBody}>
                <div className={styles.skeletonLine} style={{ width: "65%", height: 22 }} />
                <div className={styles.skeletonLine} style={{ width: "40%", height: 16 }} />
                <div className={styles.skeletonLine} style={{ width: "85%", height: 42 }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && cards.length === 0 && (
        <div className={styles.emptyStateCard}>
          <div className={styles.emptyIconWrap} aria-hidden="true">
            <svg
              width="30"
              height="30"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#166534"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect width="20" height="14" x="2" y="5" rx="2" />
              <line x1="2" x2="22" y1="10" y2="10" />
            </svg>
          </div>
          <h2 className={styles.emptyTitle}>No Subscriptions Found</h2>
          <p className={styles.emptyDesc}>
            You do not have any active auto-pay property subscriptions at this moment.
          </p>
        </div>
      )}

      {/* Subscription Cards List */}
      {!loading && cards.length > 0 && (
        <div className={styles.cardsList}>
          {cards.map((card, index) => {
            const fallback =
              FALLBACK_HOUSE_IMAGES[index % FALLBACK_HOUSE_IMAGES.length] ?? propFallback1;
            const displayImage = streetViewImages[card.propertyId] || fallback;

            return (
              <article key={card.key} className={styles.subscriptionCard}>
                {/* Left: Property Image */}
                <div className={styles.cardImageWrapper}>
                  <img
                    src={displayImage}
                    alt={card.propertyAddress}
                    className={styles.cardImage}
                    loading="lazy"
                    onError={(e) => {
                      if (e.currentTarget.src !== fallback) {
                        e.currentTarget.src = fallback;
                      }
                    }}
                  />
                </div>

                {/* Right: Card Info & Actions */}
                <div className={styles.cardContent}>
                  {/* Row 1: Home Badge + Address + Status */}
                  <div className={styles.cardHeaderRow}>
                    <div className={styles.addressGroup}>
                      <div className={styles.homeBadge} aria-hidden="true">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#166534"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                          <polyline points="9 22 9 12 15 12 15 22" />
                        </svg>
                      </div>
                      <h2 className={styles.address} title={card.propertyAddress}>
                        {card.propertyAddress}
                      </h2>
                    </div>

                    <div
                      className={`${styles.statusBadge} ${
                        card.isActive ? styles.statusActive : styles.statusInactive
                      }`}
                    >
                      <span className={styles.statusDot} aria-hidden="true" />
                      <span>{card.isActive ? "Active" : "Inactive"}</span>
                    </div>
                  </div>

                  {/* Row 2: Property ID + Copy Button */}
                  <div className={styles.propertyIdRow}>
                    <span className={styles.propertyIdLabel}>Property ID:</span>
                    <span className={styles.propertyIdValue}>{card.propertyId}</span>
                    {card.propertyId && card.propertyId !== "-" && (
                      <button
                        type="button"
                        className={styles.copyBtn}
                        onClick={() => void handleCopyPropertyId(card.propertyId)}
                        title="Copy Property ID"
                        aria-label="Copy Property ID"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                          <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Row 3: Meta Pills & Cancel Button */}
                  <div className={styles.cardFooterRow}>
                    <div className={styles.metaPillsGroup}>
                      {/* Renewal Date */}
                      <div className={styles.metaPill}>
                        <div className={styles.calendarIconBadge} aria-hidden="true">
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#2563eb"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
                            <line x1="16" x2="16" y1="2" y2="6" />
                            <line x1="8" x2="8" y1="2" y2="6" />
                            <line x1="3" x2="21" y1="10" y2="10" />
                          </svg>
                        </div>
                        <div className={styles.metaPillTexts}>
                          <span className={styles.metaLabel}>Renewal Date</span>
                          <span className={styles.metaValue}>{card.renewalDateLabel}</span>
                        </div>
                      </div>

                      {/* Annual Fee */}
                      <div className={styles.metaPill}>
                        <div className={styles.feeIconBadge} aria-hidden="true">
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#059669"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <line x1="12" x2="12" y1="2" y2="22" />
                            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                          </svg>
                        </div>
                        <div className={styles.metaPillTexts}>
                          <span className={styles.metaLabel}>Annual Fee</span>
                          <span className={styles.metaValue}>{card.annualFeeLabel}</span>
                        </div>
                      </div>
                    </div>

                    {/* Cancel Auto-Pay Button */}
                    <button
                      type="button"
                      disabled={!card.isActive || !card.subscriptionId || card.isCanceling}
                      className={`${styles.cancelActionBtn} ${
                        !card.isActive ? styles.cancelActionBtnDisabled : ""
                      }`}
                      onClick={() => void card.onCancel()}
                    >
                      <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={styles.cancelIcon}
                        aria-hidden="true"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                      </svg>
                      <span>
                        {card.isCanceling
                          ? "Canceling..."
                          : card.isActive
                            ? "Cancel Auto-Pay"
                            : "Cancelled"}
                      </span>
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};
